import { MatchStatus } from '@shared/types';
import Pill, { type PillVariant } from './Pill';

/**
 * Match-status pill — a thin wrapper over `<Pill>`. Keeps the friendly labels
 * and the deterministic variant mapping so call sites don't repeat the
 * MatchStatus → color choice.
 *
 * Palette mirrors the sub-issue spec:
 *   - active (matchmaking / in_progress) → success (green)
 *   - completed                           → info (blue)
 *   - forfeit (abandoned)                 → warning (amber)
 */

export interface StatusPillProps {
  status: MatchStatus;
}

interface StatusPresentation {
  label: string;
  /** `active` | `completed` | `forfeit` — matches the filter vocabulary. */
  kind: 'active' | 'completed' | 'forfeit';
  variant: PillVariant;
}

function presentationFor(status: MatchStatus): StatusPresentation {
  switch (status) {
    case MatchStatus.Matchmaking:
      return { label: 'Matchmaking', kind: 'active', variant: 'success' };
    case MatchStatus.InProgress:
      return { label: 'In progress', kind: 'active', variant: 'success' };
    case MatchStatus.Completed:
      return { label: 'Completed', kind: 'completed', variant: 'info' };
    case MatchStatus.Abandoned:
      return { label: 'Forfeit', kind: 'forfeit', variant: 'warning' };
    default: {
      // Exhaustive check — adding a new MatchStatus variant fails the build
      // here. Runtime fallback keeps the UI rendering.
      const _exhaustive: never = status;
      void _exhaustive;
      return {
        label: String(status),
        kind: 'completed',
        variant: 'neutral',
      };
    }
  }
}

export default function StatusPill({ status }: StatusPillProps) {
  const { label, kind, variant } = presentationFor(status);
  return (
    <Pill variant={variant} testId={`status-pill-${kind}`}>
      {label}
    </Pill>
  );
}
